/** One name policy for browser entry, stored scores, and the public relay. */
import { englishDataset, englishRecommendedTransformers, RegExpMatcher } from "obscenity";

export const DEFAULT_NAME = "Ninja";
export const MAX_NAME_LENGTH = 16;
export const EXPLICIT_NAME_ERROR = "Choose a name without explicit language.";
const MAX_CHECK_LENGTH = 256;
// Whole-name exceptions avoid known substring mistakes without exempting surrounding explicit text.
const ordinaryNames = new Set(["annalise", "analise", "dickinson", "cummins", "fukuda", "peniston"]);
const matcher = new RegExpMatcher({
  ...englishDataset.build(),
  ...englishRecommendedTransformers,
});

/** Formatting characters cannot split a word to evade the policy or disguise its displayed text. */
function normalized(value: string): string {
  return value
    .slice(0, MAX_CHECK_LENGTH)
    .normalize("NFKC")
    .replace(/\s+/g, " ")
    .replace(/[\p{Cc}\p{Cf}]/gu, "");
}

function matches(value: string): boolean {
  const plain = value.normalize("NFKD").replace(/\p{M}/gu, "").toLowerCase();
  if (ordinaryNames.has(plain.replace(/\d+$/, ""))) return false;
  if (matcher.hasMatch(value)) return true;
  // This plural is otherwise treated as a whole word, which misses obvious compound handles.
  if (plain.includes("tits")) return true;
  const words = value.replace(/(\p{Ll})(\p{Lu})/gu, "$1 $2");
  if (words !== value && matcher.hasMatch(words)) return true;
  // Names are short handles: separators must not turn a blocked word into an allowed handle.
  const joined = value.replace(/[^\p{L}\p{N}@$!|+]/gu, "");
  if (joined !== value && matcher.hasMatch(joined)) return true;
  // Common self-censoring (such as replacing a vowel with *) still denotes the same word.
  if (/[*#]/.test(value))
    for (const vowel of "aeiou") {
      const unmasked = value.replace(/[*#]/g, vowel);
      if (matcher.hasMatch(unmasked.replace(/[^\p{L}\p{N}@$!|+]/gu, ""))) return true;
    }
  return false;
}

/** Check both the supplied handle and the exact shortened handle other players would see. */
export function isExplicitName(value: string): boolean {
  const text = normalized(value).replace(/\s+/g, " ").trim();
  if (matches(text)) return true;
  return text.length > MAX_NAME_LENGTH && matches(text.slice(0, MAX_NAME_LENGTH));
}

/** The exact display form, without supplying a default or accepting it as a safe name. */
export function normalizePlayerName(value: string): string {
  const text = normalized(value).trim().slice(0, MAX_NAME_LENGTH);
  // A UTF-16 maxlength boundary must not leave a broken emoji surrogate on screen.
  return text.replace(/[\uD800-\uDBFF]$/, "");
}

/** Safe projection for old saves and untrusted peers; new name forms reject instead of replacing. */
export function cleanPlayerName(value: string): string {
  return isExplicitName(value) ? DEFAULT_NAME : normalizePlayerName(value) || DEFAULT_NAME;
}
